// src/components/VisitWorkflow.tsx
import { useState, useEffect, useRef } from 'react';
import { authFetch, rollbackVisitStage, getPatient, searchVisits, getAppointmentSlots, getTodayQueue, addToQueue, updateIntakeSession } from '../api';
import { useAuth } from '../hooks/useAuth';
import VisitStepper, { STAGES } from './VisitStepper';
import StageDataModal from './StageDataModal';
import StageEditModal from './StageEditModal';
import { PatientBanner } from './patient/PatientBanner';
import { TriageAllergyChip } from '../features/consultation/TriageAllergyChip';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { EmptyState } from './ui/States';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/Dialog';
import VerifyStage     from '../features/registration/VerifyStage';   // migrated — see HMS_V4_API_CONTRACT.md
import RegisterStage   from '../features/registration/RegisterStage'; // migrated — see HMS_V4_API_CONTRACT.md
import VitalsStage     from './stages/VitalsStage';
import PreConsultStage from './stages/PreConsultStage';
import QueueStage      from './stages/QueueStage';
import ConsultStage    from './stages/ConsultStage';
import IPStage         from './stages/IPStage';
import PharmacyStage   from './stages/PharmacyStage';
import BillingStage    from './stages/BillingStage';
import ResolveStage    from './stages/ResolveStage';
import { ConsentDueBanner } from '../features/consent/ConsentDueBanner';
import type { Visit, Patient, AppointmentSlot, QueueEntryRow, StageProps } from '../types/visit';
import { VisitCompleteSummary } from '../features/visit-summary/VisitCompleteSummary';

interface VisitWorkflowProps {
  onVisitComplete?: (visit: Visit) => void;
  onViewPatientRecord?: (uhid: string) => void;
  preselectVisitId?: string | null;
  onPreselectConsumed?: () => void;
  // Loose on purpose: App.tsx's own state for this is `useState<unknown>(null)` —
  // it carries whatever patient-shaped object Appointments' "Start intake"
  // click had on hand, cast to Patient only where it's actually consumed below.
  startPatient?: unknown;
  onStartPatientConsumed?: () => void;
  openVisitId?: string | null;
  onOpenVisitConsumed?: () => void;
  /** Set when another screen opened this visit and the user should be able to
   * go straight back to it (e.g. the Dashboard's wait-time insights). */
  backLink?: { label: string; onClick: () => void } | null;
  /** Open this visit read-only whatever its stage — for review/analysis (the
   * Dashboard's wait-time insights). Never mounts a live stage, so nothing is
   * saved, checked in or sent to AI. */
  reviewVisitId?: string | null;
  onReviewConsumed?: () => void;
}

/** A thin bar returning to the screen that opened this visit. */
function BackLinkBar({ link }: { link: { label: string; onClick: () => void } }) {
  return (
    <div className="flex items-center border-b border-line bg-card px-6 py-2">
      <button type="button" onClick={link.onClick}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12.5px] font-semibold text-teal hover:bg-muted">
        ← {link.label}
      </button>
    </div>
  );
}

/** A row from GET /api/visits/today/all or /api/visits/search — enough to
 * list and open a visit, not the full record `openVisit`'s own GET returns. */
interface VisitListRow {
  id: number;
  visit_id: string;
  name: string;
  uhid: string;
  started_at: string;
  current_stage: string;
}

// Build a local-only visit object (no DB call) so the stepper renders immediately
function makeLocalVisit(startStage: string): Visit {
  return {
    id: 0,
    visit_id:      'PENDING',
    current_stage: startStage,
    is_new_patient: true,
    stages: STAGES.map((s) => ({
      stage:  s.id,
      status: s.id === startStage ? 'active' : 'pending',
      stage_data: null,
    })),
  };
}

/** The open visit POST /api/visits/start refused to duplicate (409 open_visit_exists). */
interface OpenVisitRef {
  visit_id: string;
  current_stage: string;
  started_at: string;
}

class OpenVisitError extends Error {
  constructor(message: string, readonly openVisit: OpenVisitRef) { super(message); }
}

/** Waiting on staff: open the existing visit, or start another one anyway. */
interface OpenVisitConflict {
  patientName?: string;
  openVisit: OpenVisitRef;
  startAnyway: () => Promise<void>;
}

export default function VisitWorkflow({ onVisitComplete, onViewPatientRecord, preselectVisitId, onPreselectConsumed, startPatient, onStartPatientConsumed, openVisitId, onOpenVisitConsumed, backLink, reviewVisitId, onReviewConsumed }: VisitWorkflowProps) {
  const { branchVersion } = useAuth();
  const [visit, setVisit]     = useState<Visit | null>(null);
  // `/rollback` and `/stage/:stage/amend` both respond with a bare
  // `SELECT * FROM visits` row (+ fresh stages) — no queue/user join, so
  // fields the *main* visit fetch derives that way (assigned_doctor_id/
  // _name) simply aren't keys on that response at all. Replacing `visit`
  // wholesale with one of those responses silently drops those fields from
  // the banner/permission check until the next full reload — found live
  // while wiring up TriageAllergyChip's "Not an allergy" dismissal, which
  // goes through the same /amend endpoint. Merging instead of replacing
  // keeps everything the response *did* return, without erasing joined
  // fields it never carried in the first place (spreading an object with no
  // such key can't null one out).
  const mergeVisitUpdate = (updated: any) => setVisit(prev => (prev ? { ...prev, ...updated } : updated));
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [startMode, setStart] = useState<'new' | 'returning' | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  // A booked appointment_slot for *today*, once the patient is identified as
  // returning — carried into the Queue stage to auto-fill doctor/ordering
  // instead of asking staff to pick manually. null for a walk-in with no
  // booking (today's default behavior, unchanged).
  const [todayAppointment, setTodayAppointment] = useState<AppointmentSlot | null>(null);
  // The patient's queue row/token for *today* — created as soon as identity
  // is established (returning-patient pickup, or right after Register for a
  // new patient), not at the old "Add to queue" button deep in the stepper.
  // Gives every patient a real token from check-in onward, booked or walk-in
  // alike, instead of only once Vitals + Pre-Consult are both done.
  const [queueEntry, setQueueEntry] = useState<QueueEntryRow | null>(null);
  const [activeVisits, setActiveVisits] = useState<VisitListRow[] | null>(null); // null = loading, [] = none
  const [listError, setListError] = useState('');
  // Stage id being reviewed in StageEditModal, or null. `edit` records
  // whether the caller asked to land straight on the edit form (the Vitals
  // card's "✎ Edit" in Consultation) rather than the read-only recap the
  // stepper's own chips open — see StageProps.onViewStage's `opts.edit`.
  const [viewingStage, setViewingStage] = useState<{ stage: string; edit: boolean } | null>(null);
  const openStageModal = (stage: string, opts?: { edit?: boolean }) => setViewingStage({ stage, edit: !!opts?.edit });
  const [rollingBack, setRollingBack] = useState(false); // reason-prompt modal open?
  const [openVisitConflict, setOpenVisitConflict] = useState<OpenVisitConflict | null>(null);
  // Live count of screenings currently due, published by whichever stage
  // knows one (only Pre-Consult does today, as its checklist is filled). Held
  // here rather than inside the stage because the badge it feeds lives on the
  // banner, which is mounted above every stage. A number, not a ReactNode, so
  // a stage re-render can't loop it.
  const [screeningsDue, setScreeningsDue] = useState<number | undefined>(undefined);
  // See StageProps.reportPredictedStageSkips — only ConsultStage publishes
  // to this today. Cleared on the same stage-change effect as screeningsDue,
  // for the same reason: a stale value from the previous stage must not
  // survive into the next one.
  const [predictedSkipStages, setPredictedSkipStages] = useState<string[]>([]);
  const [rollbackReason, setRollbackReason] = useState('');
  const [rollbackBusy, setRollbackBusy] = useState(false);
  const [rollbackError, setRollbackError] = useState('');

  // Verify and Register both run entirely client-side (no DB row exists yet)
  // until registration actually succeeds — see createVisitInDB below. That
  // means the real moments those two stages started can only ever be known
  // here, in the browser; POST /api/visits/start seeds every stage's
  // activated_at as NOW() by default, which would make a new patient's
  // Registration wait/work time always compute as 0 regardless of how long
  // staff genuinely spent on either stage. These refs capture the real
  // wall-clock moments so they can be threaded through at DB-creation time.
  const verifyStartedAtRef = useRef<Date | null>(null);
  const registerStartedAtRef = useRef<Date | null>(null);

  // Visit search — looks up any visit ever (not just today's), by visit ID
  // or patient name/UHID. A visit that has actually reached 'done' is
  // genuine history and opens read-only below. A visit search finds that
  // is NOT done yet — someone's still-open work, just not from today, so it
  // fell out of "Today's active visits" — opens the exact same LIVE way that
  // list's own rows do (openVisit, defined below). Editability from there is
  // still whatever each stage's own guard allows (e.g. Consultation's
  // assigned-doctor-or-Admin check in routes/consultation.js); this screen
  // does not re-implement that, it just stops silently overriding it with a
  // blanket read-only for the sole reason that the visit wasn't found in
  // today's curated list.
  const [visitQuery, setVisitQuery]         = useState('');
  const [visitResults, setVisitResults]     = useState<VisitListRow[] | null>(null); // null = no search run yet
  const [visitSearching, setVisitSearching] = useState(false);
  const [visitSearchError, setVisitSearchError] = useState('');
  const [roVisit, setRoVisit]         = useState<Visit | null>(null); // visit being reviewed read-only, or null
  const [roLoading, setRoLoading]     = useState(false);
  const [roError, setRoError]         = useState('');
  const [viewingStageRO, setViewingStageRO] = useState<string | null>(null);

  const runVisitSearch = async () => {
    if (!visitQuery.trim()) return;
    setVisitSearching(true); setVisitSearchError('');
    try {
      const rows = await searchVisits(visitQuery.trim());
      setVisitResults(Array.isArray(rows) ? rows : []);
    } catch(e) { setVisitSearchError(e instanceof Error ? e.message : String(e)); setVisitResults([]); }
    setVisitSearching(false);
  };

  const viewVisitReadOnly = async (visitId: string, force = false) => {
    setRoLoading(true); setRoError('');
    try {
      const res  = await authFetch(`/api/visits/${visitId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load visit');
      if (data.current_stage !== 'done' && !force) {
        setRoLoading(false);
        openVisit(visitId); // eslint-disable-line @typescript-eslint/no-use-before-define -- closure over a later const, safe: only runs on a later user click
        return;
      }
      setRoVisit(data);
    } catch(e) { setRoError(e instanceof Error ? e.message : String(e)); }
    setRoLoading(false);
  };

  const loadActiveVisits = () => {
    setActiveVisits(null); setListError('');
    authFetch('/api/visits/today/all')
      .then(r => r.json())
      .then((rows: VisitListRow[]) => setActiveVisits(Array.isArray(rows) ? rows.filter(v => v.current_stage !== 'done') : []))
      .catch((e: Error) => { setListError(e.message); setActiveVisits([]); });
  };

  useEffect(() => { if (!visit) loadActiveVisits(); }, [visit, branchVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arriving here from the Dashboard's global search ("open this visit") —
  // same read-only review used by "Find a visit" below, just triggered
  // automatically instead of requiring the staff member to search again.
  useEffect(() => {
    if (preselectVisitId) { viewVisitReadOnly(preselectVisitId); onPreselectConsumed?.(); }
  }, [preselectVisitId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Review-only (wait-time insights): read-only even for a visit still in
  // progress — never the live stage, which would e.g. start a consultation's AI draft.
  useEffect(() => {
    if (reviewVisitId) { viewVisitReadOnly(reviewVisitId, true); onReviewConsumed?.(); }
  }, [reviewVisitId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arriving here from the Appointments screen's "Start intake" button on a
  // booked slot — same returning-patient bootstrap the search flow below
  // uses, just triggered directly with the already-known patient instead of
  // making staff search for someone we already identified at booking time.
  // startedForRef guards against React 18 StrictMode's dev-only double-
  // invoke of effects: without it, this would call createVisitInDB() twice
  // for the same click (a real, non-idempotent POST — unlike preselectVisitId's
  // read-only viewVisitReadOnly above), and the second insert only fails
  // harmlessly on the visit_id unique constraint by luck of the generated ID
  // colliding — the user still sees a spurious error banner.
  const startedForRef = useRef<unknown>(null);
  useEffect(() => {
    if (startPatient && startedForRef.current !== startPatient) {
      startedForRef.current = startPatient;
      startReturningVisit(startPatient as Patient & { id: number });
      onStartPatientConsumed?.();
    }
  }, [startPatient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arriving here from the Consultation screen's patient list — opens that
  // patient's actual visit live (via openVisit, same as resuming from
  // "Today's active visits" below), landing wherever its stepper currently
  // is (normally the Consultation stage itself, editable there) instead of
  // the read-only review preselectVisitId uses. Same StrictMode double-
  // invoke guard as startPatient above, even though openVisit's own GET is
  // harmless to repeat — keeps the pattern consistent and avoids a redundant
  // network round-trip.
  const openedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (openVisitId && openedForRef.current !== openVisitId) {
      openedForRef.current = openVisitId;
      openVisit(openVisitId);
      onOpenVisitConsumed?.();
    }
  }, [openVisitId]); // eslint-disable-line react-hooks/exhaustive-deps

  // If Voice Intake just extracted patient data, jump straight into a
  // pre-filled Verify stage (not Register directly) — every new-patient path
  // must run the duplicate check first, voice intake included.
  useEffect(() => {
    if (window.__voiceIntakeData && !visit) {
      verifyStartedAtRef.current = new Date();
      setVisit(makeLocalVisit('verify'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openVisit = async (visitId: string) => {
    setLoading(true); setError('');
    try {
      const res  = await authFetch(`/api/visits/${visitId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load visit');
      // The visit endpoint only carries a handful of patient fields — set
      // this minimal shape first so the stepper renders immediately, then
      // replace it with the full record (blood_group, height_cm,
      // last_weight_kg, etc.) once it arrives, same as every other path
      // into a visit already provides.
      setPatient({ id: data.patient_id, uhid: data.uhid, name: data.name, mobile: data.mobile, gender: data.gender, age: data.age });
      setVisit(data);
      if (data.uhid) getPatient(data.uhid).then(setPatient).catch(() => {});
      // Resuming an in-progress visit (e.g. from "Today's active visits")
      // skips the returning-patient bootstrap paths above, so both the
      // booked-appointment lookup and the check-in (idempotent — a no-op if
      // this visit already has a token) need to run from here too.
      if (data.patient_id) {
        const slot = await checkTodayAppointment(data.patient_id);
        checkInToQueue(data.patient_id, slot, data.id);
      }
    } catch(e) { setError(e instanceof Error ? e.message : String(e)); }
    setLoading(false);
  };

  const backToList = () => {
    setVisit(null); setPatient(null); setStart(null); setError(''); setTodayAppointment(null); setQueueEntry(null);
    // Un-link from whichever intake session this abandoned attempt carried,
    // if any — the session itself stays 'open' (untouched) so it's still
    // recoverable from VoiceIntake's list, but this stale id must not later
    // get attached to some unrelated registration that happens to succeed
    // afterward in the same browser tab.
    window.__voiceIntakeSessionId = null;
    verifyStartedAtRef.current = null;
    registerStartedAtRef.current = null;
  };

  // Stamp started_at the first time this stage's screen is actually opened —
  // separates "waiting in queue" from "staff actively working on it" for the
  // wait-time report. Idempotent server-side, so re-firing on re-render is fine.
  useEffect(() => {
    if (visit && visit.visit_id !== 'PENDING' && visit.current_stage && visit.current_stage !== 'done') {
      authFetch(`/api/visits/${visit.visit_id}/stage/${visit.current_stage}/touch`, { method: 'POST' }).catch(() => {});
    }
  }, [visit?.visit_id, visit?.current_stage]);

  const submitRollback = async () => {
    if (!rollbackReason.trim() || !visit) { setRollbackError('A reason is required'); return; }
    setRollbackBusy(true); setRollbackError('');
    try {
      const updated = await rollbackVisitStage(visit.visit_id, rollbackReason.trim());
      mergeVisitUpdate(updated);
      setRollingBack(false); setRollbackReason('');
    } catch(e) { setRollbackError(e instanceof Error ? e.message : String(e)); }
    setRollbackBusy(false);
  };

  // Derive stage map and current stage from visit
  const stageStatuses: Record<string, string> = {};
  (visit?.stages || []).forEach(s => { stageStatuses[s.stage] = s.status || 'pending'; });
  const currentStage = visit?.current_stage || 'verify';
  // Feeds the banner's triage-allergy review chip (Consultation only, 1.2's
  // Phase 2) — read directly off visit.stages here rather than threading a
  // new StageProps field through ConsultStage, since VisitWorkflow already
  // holds the whole visit and mounts PatientBanner itself.
  const preConsultStageData = visit?.stages?.find(s => s.stage === 'pre_consult')?.stage_data ?? null;

  // A screenings-due count belongs to the stage that computed it. Clear it on
  // any stage or visit change so a count from Pre-Consult can't linger on the
  // banner at Consultation, or on the next patient.
  useEffect(() => { setScreeningsDue(undefined); setPredictedSkipStages([]); }, [currentStage, visit?.id]);

  // Only offer "move back" once a real DB visit exists and there's a real,
  // non-skipped stage behind the current one to send it back to — mirrors
  // the backend's own walk-backward-past-skipped-stages logic.
  const stageIds = STAGES.map(s => s.id);
  const canRollback = (() => {
    if (!visit || visit.visit_id === 'PENDING' || visit.current_stage === 'done') return false;
    let idx = stageIds.indexOf(visit.current_stage ?? '');
    while (idx > 0) { idx -= 1; if (stageStatuses[stageIds[idx]!] !== 'skipped') return true; }
    return false;
  })();

  // ── Create visit in DB (only when we have a real patient_id) ──
  // verifyActivatedAt/registerActivatedAt (new-patient path only) are the
  // real wall-clock moments those two stages actually started, captured
  // client-side while this was still a PENDING/local-only visit — see the
  // refs' own comment above for why the backend can't know these otherwise.
  // Throws OpenVisitError when the patient already has an open visit, unless
  // confirmNewVisit — staff chose to start another one anyway.
  const createVisitInDB = async (patientId: number, isNew: boolean, verifyActivatedAt?: Date | null, registerActivatedAt?: Date | null, verifyStageData?: Record<string, unknown>, confirmNewVisit = false): Promise<Visit> => {
    const res  = await authFetch('/api/visits/start', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        patient_id: patientId, is_new_patient: isNew,
        confirm_new_visit: confirmNewVisit || undefined,
        verify_activated_at: verifyActivatedAt ? verifyActivatedAt.toISOString() : undefined,
        register_activated_at: registerActivatedAt ? registerActivatedAt.toISOString() : undefined,
        verify_stage_data: verifyStageData || undefined,
      }),
    });
    const data = await res.json();
    if (res.status === 409 && data.code === 'open_visit_exists') throw new OpenVisitError(data.error, data.open_visit);
    if (!res.ok) throw new Error(data.error || 'Failed to create visit');
    return data;
  };

  // ── Advance a stage (local + DB) ──────────────────────────────
  const completeStage = async (stage: string, stageData: Record<string, unknown> = {}, notes = '', extra: Record<string, unknown> = {}): Promise<void> => {
    if (!visit) return;
    setLoading(true); setError('');
    try {
      let updatedVisit: Visit;

      // If this is a PENDING (local-only) visit and registration just completed,
      // now we have a real patient_id — create the real DB visit first.
      if (visit.visit_id === 'PENDING' && stage === 'register' && stageData.patient_id) {
        const dbVisit = await createVisitInDB(
          stageData.patient_id as number, true, verifyStartedAtRef.current, registerStartedAtRef.current
        );
        // Mark register as completed on the DB visit, then advance to vitals
        const res2 = await authFetch(`/api/visits/${dbVisit.visit_id}/stage`, {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ stage, stage_data: stageData, notes }),
        });
        updatedVisit = await res2.json();
        if (!res2.ok) throw new Error((updatedVisit as unknown as { error?: string }).error);
        // Same check-in-at-identity-established moment as the returning-
        // patient paths — a brand new patient can't have a same-day booking
        // (they didn't exist to book one), so no appointment slot to pass.
        checkInToQueue(stageData.patient_id as number, null, dbVisit.id);
        // Registration truly succeeded — this is the moment (not the earlier
        // "Start visit with this data" click) the intake session it came
        // from, if any, is actually done. See global.d.ts's own comment.
        if (window.__voiceIntakeSessionId) {
          updateIntakeSession(window.__voiceIntakeSessionId, {
            status: 'converted', patient_id: stageData.patient_id, visit_id: dbVisit.id,
          }).catch(() => {});
          window.__voiceIntakeSessionId = null;
        }
      } else if (visit.visit_id === 'PENDING') {
        // Still no patient_id yet (e.g. verify stage on new patient path)
        // Update locally only — but still keep stage_data (e.g. the identity
        // fields collected during Verify), so later stages can read it back,
        // the same way a real DB-backed stage would.
        const stages = visit.stages || [];
        const stageIdx  = stages.findIndex(s => s.stage === stage);
        const nextStage = stages[stageIdx + 1]?.stage || 'done';
        if (nextStage === 'register') registerStartedAtRef.current = new Date();
        updatedVisit = {
          ...visit,
          current_stage: nextStage,
          stages: stages.map(s => {
            if (s.stage === stage)     return { ...s, status: 'completed', stage_data: stageData };
            if (s.stage === nextStage) return { ...s, status: 'active' };
            return s;
          }),
        };
      } else {
        // Normal DB visit — call the backend
        const res = await authFetch(`/api/visits/${visit.visit_id}/stage`, {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ ...extra, stage, stage_data: stageData, notes }),
        });
        updatedVisit = await res.json();
        if (!res.ok) throw new Error((updatedVisit as unknown as { error?: string }).error);
      }

      setVisit(updatedVisit);
      if (updatedVisit.current_stage === 'done') onVisitComplete?.(updatedVisit);
    } catch(e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setLoading(false);
  };

  // Surfaces a booked appointment_slot for *today*, used both to display it
  // on the Queue stage and to pre-fill the doctor at check-in below. Returns
  // the slot (or null) directly, rather than only setting state, so callers
  // can feed it straight into checkInToQueue without waiting on a re-render.
  // Best-effort: a lookup failure just means no booking gets pre-filled,
  // exactly like a walk-in.
  const checkTodayAppointment = async (patientId: number): Promise<AppointmentSlot | null> => {
    try {
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const slots: AppointmentSlot[] = await getAppointmentSlots({ patient_id: patientId, date: dateStr, status: 'booked' });
      const slot = slots[0] || null;
      setTodayAppointment(slot);
      return slot;
    } catch { setTodayAppointment(null); return null; }
  };

  // Mints this patient's queue token as soon as identity is known — check-in
  // time, not the old "Add to queue" button deep in the stepper (after
  // Vitals + Pre-Consult). Idempotent per visit: POST /api/queue/add returns
  // the visit's existing row, so resuming a visit (openVisit) — even one
  // started on an earlier day — never creates a second one. Looking up
  // "today's row for this patient" instead missed a visit started yesterday
  // and minted a duplicate. A lookup or creation failure is non-fatal —
  // QueueStage falls back to its own "Add to queue" button so the visit is
  // never blocked on this.
  const checkInToQueue = async (patientId: number, slot: AppointmentSlot | null, visitId: number): Promise<void> => {
    try {
      if (!visitId) {
        const existing: QueueEntryRow[] = await getTodayQueue({ patient_id: patientId });
        if (existing.length) { setQueueEntry(existing[0]!); return; }
      }
      const item = await addToQueue(patientId, {
        doctor_id: slot?.doctor_id || null,
        appointment_slot_id: slot?.id || null,
        visit_id: visitId || null,
      });
      setQueueEntry(item);
    } catch { setQueueEntry(null); }
  };

  // ── Start a RETURNING patient visit (patient already exists) ──
  const startReturningVisit = async (p: Patient & { id: number }, confirmNewVisit = false): Promise<void> => {
    setPatient(p);
    setLoading(true); setError('');
    try {
      const dbVisit = await createVisitInDB(p.id, false, null, null, undefined, confirmNewVisit);
      setVisit(dbVisit);
      const slot = await checkTodayAppointment(p.id);
      checkInToQueue(p.id, slot, dbVisit.id);
    } catch(e) {
      if (e instanceof OpenVisitError) {
        setOpenVisitConflict({ patientName: p.name, openVisit: e.openVisit, startAnyway: () => startReturningVisit(p, true) });
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
    setLoading(false);
  };

  // ── Merge: Verify stage found an existing patient record — discard the
  // pending "new patient" visit entirely and restart as a returning-patient
  // visit against the matched record (skips Register, starts at Vitals),
  // instead of letting a duplicate patient record get created.
  //
  // Typed loosely (not the exact Patient|DuplicateMatch union VerifyStage.tsx
  // declares) since both shapes only ever need `.id` here — the full patient
  // record is re-fetched via createVisitInDB -> openVisit's own getPatient
  // call on every other entry path already, so narrowing this further would
  // add nothing but a second near-identical type to keep in sync.
  const mergeAsReturningPatient = async (existingPatient: { id: number; name?: string }, confirmNewVisit = false): Promise<void> => {
    setLoading(true); setError('');
    try {
      const dbVisit = await createVisitInDB(existingPatient.id, false, null, null, undefined, confirmNewVisit);
      setPatient(existingPatient as Patient);
      setVisit(dbVisit);
      const slot = await checkTodayAppointment(existingPatient.id);
      checkInToQueue(existingPatient.id, slot, dbVisit.id);
      // Same close-out as the new-patient registration path — a merge into
      // an existing record is just as much "this intake is done" as a fresh
      // registration is.
      if (window.__voiceIntakeSessionId) {
        updateIntakeSession(window.__voiceIntakeSessionId, {
          status: 'converted', patient_id: existingPatient.id, visit_id: dbVisit.id,
        }).catch(() => {});
        window.__voiceIntakeSessionId = null;
      }
    } catch(e) {
      if (e instanceof OpenVisitError) {
        setOpenVisitConflict({ patientName: existingPatient.name, openVisit: e.openVisit, startAnyway: () => mergeAsReturningPatient(existingPatient, true) });
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
    setLoading(false);
  };

  const stageProps: StageProps = { visit: visit as Visit, patient, setPatient, completeStage, loading, onViewPatientRecord, onMergeReturning: mergeAsReturningPatient, todayAppointment, queueEntry, reportScreeningsDue: setScreeningsDue, onVisitUpdated: mergeVisitUpdate, reportPredictedStageSkips: setPredictedSkipStages, onViewStage: openStageModal };

  // Starting a visit for a patient who already has one open: continuing that
  // visit is almost always right, so it's the primary action. A second visit
  // stays possible (e.g. the open one was abandoned and is closed separately).
  const openVisitDialog = (
    <Dialog open={!!openVisitConflict} onOpenChange={(open) => { if (!open) setOpenVisitConflict(null); }}>
      {openVisitConflict && (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>This patient already has an open visit</DialogTitle>
            <DialogDescription>
              {openVisitConflict.patientName || 'This patient'} has visit {openVisitConflict.openVisit.visit_id}, started{' '}
              {new Date(openVisitConflict.openVisit.started_at).toLocaleString('en-IN')} and still at the{' '}
              {openVisitConflict.openVisit.current_stage.replace('_', ' ')} stage. Continue that visit rather than starting a second one.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" disabled={loading}
              onClick={() => { const conflict = openVisitConflict; setOpenVisitConflict(null); conflict.startAnyway(); }}>
              Start a new visit anyway
            </Button>
            <Button disabled={loading}
              onClick={() => { const code = openVisitConflict.openVisit.visit_id; setOpenVisitConflict(null); openVisit(code); }}>
              Continue {openVisitConflict.openVisit.visit_id} →
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );

  // ── Reviewing a searched visit read-only — same stepper, no live stage
  // form, no advancing. Every completed/active stage bubble opens the same
  // StageDataModal already used to browse history on a live in-progress visit.
  if (roVisit) {
    const roStageStatuses: Record<string, string> = {};
    (roVisit.stages || []).forEach(s => { roStageStatuses[s.stage] = s.status || 'pending'; });
    return (
      <div className="fade-in">
        {backLink && <BackLinkBar link={backLink} />}
        <div style={{ padding: '10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E5E7EB', flexWrap: 'wrap', gap: 8 }}>
          {backLink ? <span /> : <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => { setRoVisit(null); setRoError(''); }}>← Back to search</button>}
          <span className="badge badge-info" style={{ fontSize: 11 }}>👁 Read-only{roVisit.current_stage === 'done' ? ' — historical view' : ' — visit still in progress, nothing here changes it'}</span>
        </div>

        <VisitStepper
          currentStage={roVisit.current_stage || 'verify'}
          stageStatuses={roStageStatuses}
          visitId={roVisit.visit_id}
          onStageClick={(stageId) => { if (roStageStatuses[stageId] === 'completed' || roStageStatuses[stageId] === 'active') setViewingStageRO(stageId); }}
          onClose={backLink ? backLink.onClick : () => { setRoVisit(null); setRoError(''); }}
        />

        <div className="min-h-full bg-canvas" style={{ padding: '20px 24px' }}>
          <div className="mx-auto mb-3 max-w-[1400px] text-[12.5px] text-ink-muted">Click any completed or active stage above to see what was recorded.</div>
          <VisitCompleteSummary
            visitCode={roVisit.visit_id}
            onBackToList={backLink ? backLink.onClick : () => { setRoVisit(null); setRoError(''); }}
            backLabel={backLink ? 'Back to wait-time insights' : 'Back to search'}
            onViewPatientRecord={onViewPatientRecord}
          />
        </div>

        {viewingStageRO && (() => {
          const meta = STAGES.find(s => s.id === viewingStageRO);
          const stageEntry = (roVisit.stages || []).find(s => s.stage === viewingStageRO);
          return (
            <StageDataModal
              stageKey={viewingStageRO}
              stageLabel={meta?.label || viewingStageRO}
              stageIcon={meta?.icon || ''}
              stageEntry={stageEntry}
              visit={roVisit}
              patient={roVisit}
              onClose={() => setViewingStageRO(null)}
            />
          );
        })()}
      </div>
    );
  }

  // ── No visit started yet — show the start screen, visit search, and
  // today's active visits ─────────────────────────────────────────
  //
  // Start a patient visit and Find a visit sit side by side in one row —
  // the two things staff reach for on arrival — with Start first/leftmost
  // since it's the more frequent action (a new walk-in) and shouldn't need
  // scanning past the search card to find. Today's active visits — what
  // staff want most often once the day is underway — gets its own full-
  // width row below, so it isn't squeezed into a narrow rail (previously a
  // fixed 330px `StageLayoutRail`, which is what made a genuinely empty
  // list read as a layout bug — a large blank area beside two narrow
  // cards). The whole screen shares one `mx-auto max-w-[1560px]` shell and
  // explicit page padding, matching PreConsultStage.tsx's own top-level
  // wrapper — every child here is a plain full-width block rather than a
  // second, independently-centered StageLayout, which is what previously
  // left this screen's cards flush against the sidebar with no breathing
  // room (no wrapper here carried the page padding App.tsx suppresses for
  // `mod === 'visit'`, expecting this component to supply its own — see
  // the padded wrapper below the visit-in-progress return for the sibling
  // case that already got this right).
  if (!visit) {
    return (
      <div className="fade-in mx-auto w-full max-w-[1560px]" style={{ padding: '20px 24px' }}>
      {backLink && <div className="-mx-6 -mt-5 mb-4"><BackLinkBar link={backLink} /></div>}
      {openVisitDialog}
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2 items-start">
        <Card style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Start a patient visit</div>
          <div style={{ fontSize: 12.5, color: '#6B7280', marginBottom: 14, lineHeight: 1.5 }}>
            <b>New patients</b> go through every stage from Verify to Resolve. <b>Returning patients</b> begin directly at Vitals.
          </div>

          {error && (
            <div style={{ padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 'var(--radius)', color: '#991B1B', fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => setStart('new')}>
              👤 New patient
            </Button>
            <Button variant="ghost" onClick={() => setStart('returning')}>
              🔍 Returning patient
            </Button>
          </div>

          {/* NEW PATIENT — just set local visit, no backend call */}
          {startMode === 'new' && (
            <div style={{ textAlign: 'left', padding: '16px', marginTop: 14, background: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 'var(--radius)' }}>
              <div style={{ fontSize: 13, color: '#065F46', marginBottom: 12, fontWeight: 500 }}>
                ✓ We'll check for an existing record first, then register if needed.
              </div>
              <Button full
                onClick={() => {
                  // Create a local-only visit — no DB call, no patient_id needed yet.
                  // Starts at Verify (not Register) so every new-patient path runs
                  // the duplicate check before a new record can be created.
                  verifyStartedAtRef.current = new Date();
                  setVisit(makeLocalVisit('verify'));
                  setError('');
                }}>
                Begin verification →
              </Button>
            </div>
          )}

          {/* RETURNING PATIENT — search first, then create DB visit */}
          {startMode === 'returning' && (
            <ReturnPatientSearch
              onFound={startReturningVisit}
              loading={loading}
            />
          )}
        </Card>

        <Card>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>🔍 Find a visit</div>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>Search by visit ID or patient name — a name match lists every visit for that patient.</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <Input
              placeholder="e.g. VIS-20260722-0001 or Ananya…"
              value={visitQuery}
              onChange={e => setVisitQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && runVisitSearch()}
            />
            <Button style={{ flexShrink: 0 }} disabled={visitSearching} onClick={runVisitSearch}>
              {visitSearching ? '…' : 'Search'}
            </Button>
            {(visitQuery || visitResults !== null) && (
              <Button variant="ghost" style={{ flexShrink: 0 }}
                onClick={() => { setVisitQuery(''); setVisitResults(null); setVisitSearchError(''); }}>
                ✕ Clear
              </Button>
            )}
          </div>
          {roLoading && <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>Loading visit…</div>}
          {roError && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 10 }}>{roError}</div>}
          {visitSearchError && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 10 }}>{visitSearchError}</div>}
          {visitResults !== null && (
            visitResults.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 14, color: '#9CA3AF', fontSize: 13, background: '#F9FAFB', borderRadius: 'var(--radius)' }}>
                No visits found for "{visitQuery}"
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visitResults.map(v => (
                  <div key={v.id} onClick={() => viewVisitReadOnly(v.visit_id)}
                    style={{ padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: 'var(--radius)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--teal)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#E5E7EB'; }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{v.name} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({v.uhid})</span></div>
                      <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'var(--mono)', marginTop: 2 }}>{v.visit_id} · {new Date(v.started_at).toLocaleDateString('en-IN')}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Badge variant={v.current_stage === 'done' ? 'success' : 'info'}>{v.current_stage.replace('_', ' ')}</Badge>
                      <span style={{ color: 'var(--teal)', fontSize: 20 }}>→</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </Card>
      </div>

      <div className="mt-3.5">
        {activeVisits === null && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', fontSize: 13 }}>Loading today's visits…</div>
        )}

        {activeVisits && activeVisits.length > 0 && (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Active visits</div>
              <Button variant="ghost" size="sm" onClick={loadActiveVisits}>↻ Refresh</Button>
            </div>
            {listError && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 10 }}>{listError}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {activeVisits.map(v => (
                <div key={v.id} onClick={() => openVisit(v.visit_id)}
                  style={{ padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: 'var(--radius)', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: loading ? 0.6 : 1, transition: 'border-color .15s' }}
                  onMouseEnter={e => { if (!loading) (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--teal)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#E5E7EB'; }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{v.name} <span style={{ color: '#9CA3AF', fontWeight: 400 }}>({v.uhid})</span></div>
                    <div style={{ fontSize: 11, color: '#6B7280', fontFamily: 'var(--mono)', marginTop: 2 }}>{v.visit_id} · started {new Date(v.started_at).toDateString() === new Date().toDateString()
                      ? new Date(v.started_at).toLocaleTimeString('en-IN')
                      : new Date(v.started_at).toLocaleString('en-IN')}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Badge variant="info">{v.current_stage.replace('_',' ')}</Badge>
                    <span style={{ color: 'var(--teal)', fontSize: 20 }}>→</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* activeVisits loaded (not null) but empty. */}
        {activeVisits && activeVisits.length === 0 && (
          <EmptyState label="No active visits today" hint="A visit started today will appear here." />
        )}
      </div>
      </div>
    );
  }

  // ── Visit in progress — show stepper + current stage ──────────
  return (
    <div className="fade-in">
      {backLink && <BackLinkBar link={backLink} />}
      <VisitStepper
        currentStage={currentStage}
        stageStatuses={stageStatuses}
        visitId={visit.visit_id !== 'PENDING' ? visit.visit_id : null}
        onStageClick={(stageId) => { if (stageStatuses[stageId] === 'completed') openStageModal(stageId); }}
        onClose={currentStage !== 'done' ? backToList : null}
        canRollback={canRollback}
        onRollback={() => { setRollingBack(true); setRollbackReason(''); setRollbackError(''); }}
        predictedSkip={predictedSkipStages}
      />

      {/* Persistent identity — mounted once here, inherited by every stage
          below rather than each one rendering (or not rendering) identity
          on its own. Renders nothing until a patient is actually known
          (Verify/early Register), and nothing extra is fetched: every field
          is already on `visit`/`patient`/`queueEntry`.
          See docs/HMS_V4_UI_MODERNIZATION_ASSESSMENT.md backlog item 1. */}
      <PatientBanner
        patient={patient}
        visit={visit}
        stageLabel={STAGES.find(s => s.id === currentStage)?.label}
        queueEntry={queueEntry}
        screeningsDue={screeningsDue}
        onViewPatientRecord={onViewPatientRecord}
        extra={currentStage === 'consultation' && patient?.uhid && visit?.visit_id ? (
          <TriageAllergyChip
            uhid={patient.uhid}
            visitId={visit.visit_id}
            preConsult={preConsultStageData}
            onReconciled={mergeVisitUpdate}
          />
        ) : null}
      />

      {openVisitDialog}

      {rollingBack && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200 }}>
          <div className="card" style={{ maxWidth:440, width:'90%', padding:20 }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:8 }}>Move visit back a stage</div>
            <div style={{ fontSize:13, color:'#6B7280', marginBottom:14 }}>
              This sends the visit back to the previous stage for correction. Please explain why.
            </div>
            {rollbackError && <div style={{ marginBottom:12, padding:'8px 12px', background:'#FEE2E2', color:'#991B1B', borderRadius:'var(--radius)', fontSize:12 }}>{rollbackError}</div>}
            <textarea autoFocus style={{ resize:'vertical', height:80, width:'100%', marginBottom:14 }}
              placeholder="Reason for moving back… (required)"
              value={rollbackReason} onChange={e=>setRollbackReason(e.target.value)} />
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button className="btn-ghost" disabled={rollbackBusy} onClick={()=>setRollingBack(false)}>Cancel</button>
              <button className="btn-primary" disabled={rollbackBusy} onClick={submitRollback}>
                {rollbackBusy ? 'Moving…' : 'Confirm move back'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingStage && visit.visit_id !== 'PENDING' && (() => {
        const meta = STAGES.find(s => s.id === viewingStage.stage);
        const stageEntry = (visit.stages || []).find(s => s.stage === viewingStage.stage);
        return (
          <StageEditModal
            stageKey={viewingStage.stage}
            startInEdit={viewingStage.edit}
            stageLabel={meta?.label || viewingStage.stage}
            stageIcon={meta?.icon || ''}
            stageEntry={stageEntry}
            visitId={visit.visit_id}
            visit={visit}
            patient={patient ?? undefined}
            onClose={() => setViewingStage(null)}
            onVisitUpdated={mergeVisitUpdate}
          />
        );
      })()}

      {error && (
        <div style={{ margin: '12px 24px', padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 'var(--radius)', color: '#991B1B', fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}

      {/* Renders nothing unless a consent has lapsed or was never taken — the
          re-ask path for a RETURNING patient, who skips Verify and Register
          and so never reaches the Registration consent step. Not shown during
          Register itself, where that step is already asking. */}
      {currentStage !== 'register' && <ConsentDueBanner uhid={patient?.uhid} />}

      {/* Explicit canvas ground: every stage below renders white cards, and
          white-on-white leaves them with only a hairline border to separate
          them. Named token, so it repaints per hospital and inverts in dark
          mode rather than being a fixed grey. */}
      <div className="min-h-full bg-canvas" style={{ padding: '20px 24px' }}>
        {currentStage === 'verify'       && <VerifyStage     completeStage={completeStage} loading={loading} onMergeReturning={mergeAsReturningPatient} />}
        {currentStage === 'register'     && <RegisterStage   visit={visit} completeStage={completeStage} loading={loading} setPatient={setPatient as (patient: Patient) => void} />}
        {currentStage === 'vitals'       && <VitalsStage     {...stageProps} />}
        {currentStage === 'pre_consult'  && <PreConsultStage {...stageProps} />}
        {currentStage === 'queue'        && <QueueStage      {...stageProps} />}
        {currentStage === 'consultation' && <ConsultStage    {...stageProps} />}
        {currentStage === 'ip'           && <IPStage         {...stageProps} />}
        {currentStage === 'pharmacy'     && <PharmacyStage   {...stageProps} />}
        {currentStage === 'billing'      && <BillingStage    {...stageProps} />}
        {currentStage === 'resolve'      && <ResolveStage    {...stageProps} />}
        {currentStage === 'done'         && <VisitCompleteSummary visitCode={visit.visit_id} onBackToList={backToList} onViewPatientRecord={onViewPatientRecord} />}
      </div>
    </div>
  );
}

// ── Returning patient search ───────────────────────────────────
function ReturnPatientSearch({ onFound, loading }: { onFound: (p: Patient & { id: number }) => void; loading: boolean }) {
  const [q, setQ]           = useState('');
  const [results, setResults] = useState<Array<Patient & { id: number }>>([]);
  const [searching, setS]   = useState(false);
  const [searched, setSearched] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setS(true); setSearched(false);
    try {
      const res  = await authFetch(`/api/patients/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
    } catch {
      setResults([]);
    }
    setS(false); setSearched(true);
  };

  return (
    <div style={{ textAlign: 'left', marginTop: 8 }}>
      <label className="lbl">Search by name, UHID or mobile</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          placeholder="e.g. UH2501 or Ananya or 9876…"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
        />
        <button className="btn-primary" onClick={search} disabled={searching || loading}
          style={{ padding: '8px 16px', flexShrink: 0 }}>
          {searching ? '…' : 'Search'}
        </button>
      </div>

      {results.map(p => {
        // A deceased patient is listed (so staff can see why they can't be
        // picked) but not selectable. The server refuses the visit anyway
        // (POST /api/visits/start → 409 patient_deceased); this saves the click.
        const blocked = loading || !!p.deceased;
        return (
          <div key={p.id} onClick={() => { if (!p.deceased) onFound(p); }}
            title={p.deceased ? 'This patient is recorded as deceased — a new visit cannot be started' : undefined}
            style={{ padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: 'var(--radius)', marginBottom: 6, cursor: blocked ? 'not-allowed' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: blocked ? 0.6 : 1 }}
            onMouseEnter={e => { if (!blocked) (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--teal)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#E5E7EB'; }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {p.name}
                {p.deceased && <Badge variant="neutral" className="ml-2 align-middle">Deceased</Badge>}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280' }}>
                {p.uhid} · {p.gender}, {p.age}y · {p.mobile}
              </div>
            </div>
            {!p.deceased && <span style={{ color: 'var(--teal)', fontSize: 20 }}>→</span>}
          </div>
        );
      })}

      {searched && results.length === 0 && (
        <div style={{ textAlign: 'center', padding: '16px', color: '#9CA3AF', fontSize: 13, background: '#F9FAFB', borderRadius: 'var(--radius)' }}>
          No patients found for "{q}"
        </div>
      )}
    </div>
  );
}
